import { useState } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Custom hook for fetching company-wide data via Edge Function
 * This bypasses RLS issues by using server-side service role access
 * while maintaining security through server-side authorization
 */
export const useCompanyData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch company data from the Edge Function
   * @param {string} dataType - Type of data to fetch: 'metrics', 'orders', 'charts', or 'recent'
   * @returns {Promise<Object>} The requested data
   */
  const fetchCompanyData = async (dataType) => {
    setLoading(true);
    setError(null);

    try {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session');
      }

      // Call the Edge Function
      const { data, error: functionError } = await supabase.functions.invoke('get-company-data', {
        body: { dataType },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (functionError) {
        console.error('Edge function error:', functionError);
        throw functionError;
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch data');
      }

      return data.data;
    } catch (err) {
      console.error('Error fetching company data:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { fetchCompanyData, loading, error };
};
